import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Shield, 
  History, 
  User, 
  Calendar,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  Clock,
  AlertTriangle,
  TrendingUp
} from 'lucide-react';

const PermissionsLogs = () => {
  const { language } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    userId: '',
    startDate: '',
    endDate: ''
  });
  const [expandedLog, setExpandedLog] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchSummary();
    fetchLogs();
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSummary = async () => {
    try {
      const res = await api.get('/detailed-reports/permissions-logs/summary');
      setSummary(res.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.userId) params.append('user_id', filters.userId);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);

      const res = await api.get(`/detailed-reports/permissions-logs?${params.toString()}`);
      setLogs(res.data.logs);
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل تحميل السجلات' : 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/permissions/users');
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return language === 'ar' 
      ? date.toLocaleDateString('ar-AE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getActionLabel = (action) => {
    const labels = {
      'view': language === 'ar' ? 'عرض' : 'View',
      'add': language === 'ar' ? 'إضافة' : 'Add',
      'edit': language === 'ar' ? 'تعديل' : 'Edit',
      'delete': language === 'ar' ? 'حذف' : 'Delete'
    };
    return labels[action] || action;
  };

  const getSectionLabel = (section) => {
    const labels = {
      'revenues': language === 'ar' ? 'الإيرادات' : 'Revenues',
      'expenses': language === 'ar' ? 'المصروفات' : 'Expenses',
      'inventory': language === 'ar' ? 'المخزون' : 'Inventory',
      'appointments': language === 'ar' ? 'المواعيد' : 'Appointments',
      'reports': language === 'ar' ? 'التقارير' : 'Reports',
      'barbers': language === 'ar' ? 'الحلاقين' : 'Barbers',
      'housing': language === 'ar' ? 'السكن' : 'Housing',
      'ratings': language === 'ar' ? 'التقييمات' : 'Ratings',
      'settings': language === 'ar' ? 'الإعدادات' : 'Settings',
      'permissions': language === 'ar' ? 'الصلاحيات' : 'Permissions'
    };
    return labels[section] || section;
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-purple-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {language === 'ar' ? 'سجل تغييرات الصلاحيات' : 'Permissions Change Log'}
            </h1>
            <p className="text-muted-foreground">
              {language === 'ar' ? 'تتبع من قام بتعديل ماذا ومتى' : 'Track who changed what and when'}
            </p>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-card border border-white/5 rounded-xl p-4">
              <div className="text-sm text-muted-foreground mb-1">
                {language === 'ar' ? 'إجمالي التغييرات' : 'Total Changes'}
              </div>
              <div className="text-3xl font-bold text-primary">{summary.total_changes}</div>
            </div>
            <div className="bg-card border border-white/5 rounded-xl p-4">
              <div className="text-sm text-muted-foreground mb-1">
                {language === 'ar' ? 'هذا الشهر' : 'This Month'}
              </div>
              <div className="text-3xl font-bold text-blue-500">{summary.changes_this_month}</div>
            </div>
            <div className="bg-card border border-white/5 rounded-xl p-4">
              <div className="text-sm text-muted-foreground mb-1">
                {language === 'ar' ? 'هذا الأسبوع' : 'This Week'}
              </div>
              <div className="text-3xl font-bold text-green-500">{summary.changes_this_week}</div>
            </div>
            <div className="bg-card border border-white/5 rounded-xl p-4">
              <div className="text-sm text-muted-foreground mb-1">
                {language === 'ar' ? 'طلبات معلقة' : 'Pending Requests'}
              </div>
              <div className="text-3xl font-bold text-yellow-500">{summary.pending_requests}</div>
            </div>
          </div>
        )}

        {/* Most Changed Users */}
        {summary?.most_changed_users?.length > 0 && (
          <div className="bg-card border border-white/5 rounded-xl p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              {language === 'ar' ? 'أكثر المستخدمين تغييراً' : 'Most Changed Users'}
            </h3>
            <div className="flex flex-wrap gap-2">
              {summary.most_changed_users.map((u) => (
                <div key={u.user_id || u.user_name} className="bg-secondary/50 rounded-lg px-3 py-2 text-sm">
                  <span className="font-medium">{u.user_name}</span>
                  <span className="text-muted-foreground mx-2">•</span>
                  <span className="text-primary">{u.changes} {language === 'ar' ? 'تغيير' : 'changes'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-card border border-white/5 rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'ar' ? 'المستخدم' : 'User'}
              </label>
              <select
                value={filters.userId}
                onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                className="w-full h-10 px-3 rounded-md bg-secondary/50 border border-white/10"
              >
                <option value="">{language === 'ar' ? 'الكل' : 'All'}</option>
                {users.map((u) => (
                  <option key={u.user.id} value={u.user.id}>{u.user.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'ar' ? 'من تاريخ' : 'From Date'}
              </label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="bg-secondary/50 border-white/10"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'ar' ? 'إلى تاريخ' : 'To Date'}
              </label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="bg-secondary/50 border-white/10"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={fetchLogs} className="w-full gap-2">
                <History className="w-4 h-4" />
                {language === 'ar' ? 'بحث' : 'Search'}
              </Button>
            </div>
          </div>
        </div>

        {/* Logs List */}
        <div className="bg-card border border-white/5 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-white/5">
            <h2 className="font-semibold flex items-center gap-2">
              <History className="w-5 h-5" />
              {language === 'ar' ? 'سجل التغييرات' : 'Change Log'}
              <span className="text-sm text-muted-foreground">({logs.length})</span>
            </h2>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              {language === 'ar' ? 'لا توجد سجلات' : 'No logs found'}
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {logs.map((log) => (
                <div key={log.id}>
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5"
                    onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-purple-500/10 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-purple-500" />
                      </div>
                      <div>
                        <div className="font-medium">
                          {log.admin_name} → {log.target_user_name}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          {formatDate(log.created_at)}
                          {log.approval_mode === 'pending_approved' && (
                            <span className="text-green-500 text-xs">({language === 'ar' ? 'بعد الموافقة' : 'After approval'})</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm bg-primary/20 text-primary px-2 py-1 rounded">
                        {log.changes_count} {language === 'ar' ? 'تغيير' : 'changes'}
                      </span>
                      {expandedLog === log.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </div>

                  {expandedLog === log.id && log.changes && (
                    <div className="bg-secondary/30 p-4 space-y-2">
                      {log.changes.map((change, i) => (
                        <div key={`${change.section}-${change.action}-${i}`} className="flex items-center gap-3 text-sm">
                          <span className="font-medium">{getSectionLabel(change.section)}</span>
                          <span className="text-muted-foreground">•</span>
                          <span>{getActionLabel(change.action)}</span>
                          <span className="text-muted-foreground">:</span>
                          <span className={`flex items-center gap-1 ${change.old_value ? 'text-red-400' : 'text-muted-foreground'}`}>
                            {change.old_value ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          </span>
                          <span className="text-muted-foreground">→</span>
                          <span className={`flex items-center gap-1 ${change.new_value ? 'text-green-400' : 'text-muted-foreground'}`}>
                            {change.new_value ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionsLogs;
